import { cx } from "@linaria/core"
import { styled } from "@linaria/react"
import { useNavigate, useRouter } from "@tanstack/react-router"
import { runInAction } from "mobx"
import { observer } from "mobx-react-lite"
import { type ChangeEvent, useCallback, useState } from "react"
import { useHotkeys } from "react-hotkeys-hook"

import { Centered } from "@/components/Centered/Centered.ts"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/DropdownMenu/DropdownMenu.tsx"
import { DropdownMenuStyles } from "@/components/DropdownMenu/DropdownMenuStyles.ts"
import { Ellipsis, EllipsisContainer } from "@/components/Ellipsis/Ellipsis.ts"
import { Horizontal, Spring, Vertical } from "@/components/Flex.ts"
import { GradientBlob } from "@/components/GradientBlob.ts"
import { BatchLoader } from "@/components/IntersectionObserver/BatchLoader.tsx"
import { MarkdownWrapper } from "@/components/Markdown/MarkdownWrapper.tsx"
import { PromptInput } from "@/components/PromptInput/PromptInput.tsx"
import { Scrollable } from "@/components/Scrollable/Scrollable.tsx"
import { Spinner } from "@/components/Spinner/Spinner.tsx"
import { useDevMode } from "@/hooks/useDevMode.ts"
import { useCloseLocation, useProjectLocation } from "@/hooks/useMatterLocation.ts"
import { useModel } from "@/hooks/useModel.ts"
import { useMyProfile } from "@/hooks/useProfile.ts"
import { useInvalidateQueries, useOrgContext, useQueries } from "@/hooks/useRootContext.ts"
import { CheckmarkMedium, ChevronDownRegular, CloseLight, Icon20 } from "@/icons/Icons.ts"
import { pageTitle } from "@/lib/displayUtils.ts"
import { startViewTransition } from "@/lib/dom.ts"
import { normalizeProjectKey } from "@/lib/std.ts"
import { Page } from "@/routes/auth/Page.tsx"
import { HomeStyles } from "@/routes/home/HomeStyles.ts"
import { Navigator } from "@/routes/Navigator.ts"
import { useCurrentProject } from "@/routes/projects/projectHooks.ts"
import { ProjectListModel } from "@/routes/projects/ProjectListModel.ts"
import { NewTaskPromptInputModel } from "@/routes/tasks/TaskNewScene/NewTaskPromptInputModel.ts"
import { LAST_USED_PROJECT_KEY } from "@/routes/tasks/TaskNewScene/persistence.ts"
import { tasksByRepoKey, tasksByUserKey, useNewTaskRoute, useTasksQueryFilters } from "@/routes/tasks/tasksHooks.ts"
import { mobileBreakpoint, Rotate0, Rotate180 } from "@/styles/mixins.ts"
import { ellipsize } from "@/styles/textStyles.ts"

import type { PR_Project2 } from "@/lib/client/generated/TypeDefinitions.ts"
import type { TaskSettingsModel } from "@/routes/tasks/TaskNewScene/TaskSettingsModel.ts"


export const TaskNewScene = observer(() => {
  const devMode = useDevMode()
  const queries = useQueries()
  const invalidateQueries = useInvalidateQueries()

  const context = useOrgContext()
  const navigate = useNavigate()
  const router = useRouter()

  const project = useCurrentProject()
  const projectKey = project.key

  const search = useNewTaskRoute().useSearch()

  const tasksQueryFilters = useTasksQueryFilters()
  const myProfile = useMyProfile()

  const closeLocation = useCloseLocation(useProjectLocation(project.key))

  const leave = () => startViewTransition(() => navigate(closeLocation))

  const newTaskModel = useModel(() => new ProjectListModel(context))

  const handleProjectSelect = (selectedProject: PR_Project2) => {
    // No transition is needed, we're staying on the same page
    void navigate(Navigator.newTask(context.orgId, selectedProject.key, {
      onboarding: search.onboarding,
      closeLocation: { href: router.state.location.href }
    }))
  }

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const promptInputModel = useModel(() => {
    return new NewTaskPromptInputModel(
      router,
      context,
      project,
      async () => {
        const lastUsedProjectKey = `${context.orgId}/${LAST_USED_PROJECT_KEY}`
        void queries.apiClient.setProfilePreference(lastUsedProjectKey, normalizeProjectKey(project.key))
        void invalidateQueries({ queryKey: ["getProfilePreference", lastUsedProjectKey] })
        void invalidateQueries(tasksByRepoKey(project.key))
        if (myProfile) {
          void invalidateQueries(tasksByUserKey(myProfile.id))
        }
        void invalidateQueries(tasksQueryFilters)
      },
      async (task, view, tab) => {
        startViewTransition(() => navigate(Navigator.task(context.orgId, project.key, task.id, { view, tab })))
      }
    )
  }, [normalizeProjectKey(project.key)])

  useHotkeys("Esc", () => {
    const input = promptInputModel.inputModel.inputRef.current

    if (input) {
      if (document.activeElement === input) {
        input.blur()
      } else if (!input.value) {
        leave()
      }
    }
  }, { enableOnFormTags: true })

  return (
    <Page>
      <title>{pageTitle("New idea", project.name)}</title>

      {newTaskModel.projects.get().length > 1
        ? (
          <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <PageTitle>
                <Horizontal data-testid="project-dropdown-selector" inline alignItems="center" gap="4px">
                  <NewIdeaPrefix>New idea in</NewIdeaPrefix>
                  <EllipsisContainer><Ellipsis>{project.name}</Ellipsis></EllipsisContainer>

                  <SlidingChevron>
                    <ChevronDownRegular className={cx(isDropdownOpen ? Rotate180 : Rotate0)} />
                  </SlidingChevron>
                </Horizontal>
              </PageTitle>
            </DropdownMenuTrigger>
            {isDropdownOpen && (
              <DropdownMenuContent>
                <ProjectListScrollable>
                  {newTaskModel.projects.get().map((project) => {
                    const isCurrentProject = normalizeProjectKey(projectKey) === normalizeProjectKey(project.key)
                    return (
                      <DropdownMenuItem
                        key={project.id}
                        onClick={() => handleProjectSelect(project)}
                        className={cx(isCurrentProject ? DropdownMenuStyles.SelectedItem : null)}
                        data-testid={project.name}
                      >
                        <Horizontal alignItems="center" gap="4px">
                          <EllipsisContainer><Ellipsis>{project.name}</Ellipsis></EllipsisContainer>
                          <Spring />
                          {isCurrentProject && <CheckmarkMedium className={Icon20} />}
                        </Horizontal>
                      </DropdownMenuItem>
                    )
                  })}

                  {newTaskModel.isLoading.get() && <Spinner text={ellipsize("Loading")} />}

                  <BatchLoader
                    setBatch={() => void newTaskModel.loadNextBatch()}
                    batchSize={newTaskModel.batchSize}
                    nextOffset={newTaskModel.nextOffset.get()}
                    hasMore={newTaskModel.hasMore.get()}
                  />
                </ProjectListScrollable>
              </DropdownMenuContent>
            )}
          </DropdownMenu>
        )
        : (
          <PageTitle><EllipsisContainer><Ellipsis>New idea in {project.name}</Ellipsis></EllipsisContainer></PageTitle>
        )
      }

      <HomeStyles.PageCloseButton data-testid="close-new-idea-button" kind="icon" onClick={leave}>
        <CloseLight />
      </HomeStyles.PageCloseButton>

      <PromptCentered>
        <PromptTitle data-testid="welcome-text">
          What matters to you?
        </PromptTitle>

        <LimitedWidthInput>
          <PromptInput
            model={promptInputModel.inputModel}
            placeholder="Describe your idea"
            projectKey={project.key}
            repositoryName={project.repository.name}
          />

          {devMode && (
            <TaskNewSceneDevPanel model={promptInputModel.taskSettings} />
          )}
        </LimitedWidthInput>
      </PromptCentered>

      <GradientBlob />
    </Page>
  )
})

export const TaskNewSceneDevPanel = (props: { model: TaskSettingsModel }) => {
  return (
    <DevPanel>
      <BrowserVerificationCheckbox model={props.model} />
      <MockCheckbox model={props.model} />
      <MultiIterationLLMHistoryCheckbox model={props.model} />
      {props.model.isNewProject && (
        <>
          <GenerateGuidelinesInOnboardingCheckbox model={props.model} />
        </>
      )}
    </DevPanel>
  )
}

const BrowserVerificationCheckbox = observer((props: { model: TaskSettingsModel }) => {
  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    runInAction(() => {
      props.model.useBrowserVerification.set(event.target.checked)
    })
  }, [props.model])

  return (
    <FullWidthFieldRow>
      <FieldApplyLabel>
        <input type="checkbox" checked={props.model.useBrowserVerification.get()} onChange={handleChange} />
        <MarkdownWrapper text="Verify in the browser after coding" inlineOnly />
      </FieldApplyLabel>
    </FullWidthFieldRow>
  )
})

const MockCheckbox = observer((props: { model: TaskSettingsModel }) => {
  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    runInAction(() => {
      props.model.mockForTesting.set(event.target.checked)
    })
  }, [props.model])
  return (
    <FullWidthFieldRow>
      <FieldApplyLabel>
        <input type="checkbox" checked={props.model.mockForTesting.get()} onChange={handleChange} />
        <MarkdownWrapper text="Mock task for UI tests" inlineOnly />
      </FieldApplyLabel>
    </FullWidthFieldRow>
  )
})

const MultiIterationLLMHistoryCheckbox = observer((props: { model: TaskSettingsModel }) => {
  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    runInAction(() => {
      props.model.useMultiIterationLLMHistory.set(event.target.checked)
    })
  }, [props.model])
  return (
    <FullWidthFieldRow>
      <FieldApplyLabel>
        <input type="checkbox" checked={props.model.useMultiIterationLLMHistory.get()} onChange={handleChange} />
        <MarkdownWrapper text="Multi-iteration LLM history" inlineOnly />
      </FieldApplyLabel>
    </FullWidthFieldRow>
  )
})

const GenerateGuidelinesInOnboardingCheckbox = observer((props: { model: TaskSettingsModel }) => {
  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    runInAction(() => {
      props.model.generateGuidelinesInOnboarding.set(event.target.checked)
    })
  }, [props.model])
  return (
    <FullWidthFieldRow>
      <FieldApplyLabel>
        <input type="checkbox" checked={props.model.generateGuidelinesInOnboarding.get()} onChange={handleChange} />
        <MarkdownWrapper text="Generate guidelines in onboarding" inlineOnly />
      </FieldApplyLabel>
    </FullWidthFieldRow>
  )
})

const FieldApplyLabel = styled.label`
  display: flex;
  align-items: baseline;
  gap: 8px;
`

const PageTitle = styled(HomeStyles.PageTitle)`
  align-self: center;
  view-transition-name: new-task-h1;
  white-space: nowrap;
`

const NewIdeaPrefix = styled.span`
  color: var(--secondary-text-color);
`

const SlidingChevron = styled(Horizontal)`
  align-items: center;
  justify-content: center;

  @media (prefers-reduced-motion: no-preference) {
    opacity: 0;
    transform: translateY(-10px);
    animation: chevron-fade-in 300ms ease-out 300ms forwards;
  }

  view-transition-name: new-task-chevron;

  @keyframes chevron-fade-in {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`

const PromptCentered = styled(Centered)`
  margin-top: -48px;
  padding-top: 48px;
`

const PromptTitle = styled(HomeStyles.PageSubtitle)`
  view-transition-name: new-task-h2;
`

const AnimatedContent = styled.div`
  width: 100%;

  view-transition-name: new-task-content;
`

const LimitedWidthInput = styled(AnimatedContent)`
  max-width: var(--chat-width);
`

const FieldRow = styled(Vertical)`
  gap: 8px;

  width: 100%;
  max-width: var(--chat-width);
  padding-top: 24px;
`

const FullWidthFieldRow = styled(FieldRow)`

`

const DevPanel = styled(Horizontal)`
  flex-wrap: wrap;
  gap: 0 16px;

  & > ${FieldRow}:not(${FullWidthFieldRow}) {
    max-width: calc(50% - 8px);
  }

  @media ${mobileBreakpoint} {
    & > ${FieldRow} {
      max-width: 100%;
    }
  }
`

const ProjectListScrollable = styled(Scrollable)`
  max-height: 50vh;
`